import { Form, Select } from 'antd';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';
import { allowanceCurrencyRule } from './allowanceCurrencyRule';

/** Meal, holiday, travel and work-from-home allowances with currencies. */
export default function CompanyAllowancesFields() {
  const {
    currencyOptions,
    getTranslatedCalculationOptionsForType,
    hasBranchCompanyType,
    isProfile,
    holidayAllowanceCurrencyKeys,
    holidayAllowanceCurrencyState,
    holidayAllowanceKeys,
    holidayAllowanceState,
    mealAllowanceCurrencyKeys,
    mealAllowanceCurrencyState,
    mealAllowanceKeys,
    mealAllowanceState,
    sectionClass,
    shouldWorkFromHomeBeVisible,
    t,
    travelAllowanceCurrencyKeys,
    travelAllowanceCurrencyState,
    travelAllowanceKeys,
    travelAllowanceState,
    workFromHomeAllowanceCurrencyKeys,
    workFromHomeAllowanceCurrencyState,
    workFromHomeAllowanceKeys,
    workFromHomeAllowanceState,
  } = useCompanyGeneralDataForm();
  // On the profile: laid out by CompanyAllowancesProfilePanel.
  if (isProfile) return null;
  const currencyRequiredWhenUsed = (methodKeys) =>
    allowanceCurrencyRule(
      methodKeys,
      t('formValidation.currencyRequired', { ns: 'forms' })
    );

  return (
    <>
      {!hasBranchCompanyType && (
        <div className={sectionClass('naknade')}>
          <div className="grid grid-cols-[1fr_250px] gap-x-5">
            {mealAllowanceState?.visible && (
              <Form.Item
                name={mealAllowanceKeys}
                label={t('calculationMethodMeal', { ns: 'forms' })}
                rules={[
                  {
                    required: true,
                    message: t('formValidation.selectRequired', {
                      ns: 'forms',
                    }),
                  },
                ]}
              >
                <Select
                  allowClear
                  mode="single"
                  placeholder={t('calculationMethodMeal', { ns: 'forms' })}
                  className="w-full"
                  loading={false}
                  options={getTranslatedCalculationOptionsForType('meal', t)}
                  disabled={mealAllowanceState?.disabled}
                />
              </Form.Item>
            )}

            {mealAllowanceCurrencyState?.visible && (
              <Form.Item
                name={mealAllowanceCurrencyKeys}
                label={t('mealCurrency', { ns: 'forms' })}
                dependencies={[mealAllowanceKeys]}
                rules={[currencyRequiredWhenUsed(mealAllowanceKeys)]}
              >
                <Select
                  mode="single"
                  placeholder={t('mealCurrency', { ns: 'forms' })}
                  className="w-full"
                  loading={false}
                  options={currencyOptions}
                  disabled={mealAllowanceCurrencyState?.disabled}
                />
              </Form.Item>
            )}
          </div>

          {holidayAllowanceState?.visible ||
          holidayAllowanceCurrencyState?.visible ? (
            <hr className="mb-5" />
          ) : null}

          <div className="grid grid-cols-[1fr_250px] gap-x-5">
            {holidayAllowanceState?.visible && (
              <Form.Item
                name={holidayAllowanceKeys}
                label={t('calculationMethodHoliday', { ns: 'forms' })}
                rules={[
                  {
                    required: true,
                    message: t('formValidation.selectRequired', {
                      ns: 'forms',
                    }),
                  },
                ]}
              >
                <Select
                  allowClear
                  mode="single"
                  placeholder={t('calculationMethodHoliday', { ns: 'forms' })}
                  className="w-full"
                  loading={false}
                  options={getTranslatedCalculationOptionsForType('holiday', t)}
                  disabled={holidayAllowanceState?.disabled}
                />
              </Form.Item>
            )}

            {holidayAllowanceCurrencyState?.visible && (
              <Form.Item
                name={holidayAllowanceCurrencyKeys}
                label={t('holidayCurrency', { ns: 'forms' })}
                dependencies={[holidayAllowanceKeys]}
                rules={[currencyRequiredWhenUsed(holidayAllowanceKeys)]}
              >
                <Select
                  mode="single"
                  placeholder={t('holidayCurrency', { ns: 'forms' })}
                  className="w-full"
                  loading={false}
                  options={currencyOptions}
                  disabled={holidayAllowanceCurrencyState?.disabled}
                />
              </Form.Item>
            )}
          </div>

          {travelAllowanceState?.visible ||
          travelAllowanceCurrencyState?.visible ? (
            <hr className="mb-5" />
          ) : null}

          <div className="grid grid-cols-[1fr_250px] gap-x-5">
            {travelAllowanceState?.visible && (
              <Form.Item
                name={travelAllowanceKeys}
                label={t('travelExpensesMethod', { ns: 'forms' })}
                rules={[
                  {
                    required: true,
                    message: t('formValidation.selectRequired', {
                      ns: 'forms',
                    }),
                  },
                ]}
              >
                <Select
                  allowClear
                  mode="single"
                  placeholder={t('travelExpensesMethod', { ns: 'forms' })}
                  className="w-full"
                  loading={false}
                  options={getTranslatedCalculationOptionsForType('travel', t)}
                  disabled={travelAllowanceState?.disabled}
                />
              </Form.Item>
            )}

            {travelAllowanceCurrencyState?.visible && (
              <Form.Item
                name={travelAllowanceCurrencyKeys}
                label={t('travelExpCurrency', { ns: 'forms' })}
                dependencies={[travelAllowanceKeys]}
                rules={[currencyRequiredWhenUsed(travelAllowanceKeys)]}
              >
                <Select
                  mode="single"
                  placeholder={t('travelExpCurrency', { ns: 'forms' })}
                  className="w-full"
                  loading={false}
                  options={currencyOptions}
                  disabled={travelAllowanceCurrencyState?.disabled}
                />
              </Form.Item>
            )}
          </div>

          {workFromHomeAllowanceState?.visible ||
          workFromHomeAllowanceCurrencyState?.visible ? (
            <hr className="mb-5" />
          ) : null}

          {shouldWorkFromHomeBeVisible && (
            <>
              <div className="grid grid-cols-[1fr_250px] gap-x-5">
                {workFromHomeAllowanceState?.visible && (
                  <Form.Item
                    name={workFromHomeAllowanceKeys}
                    label={t('workFromHomeMethod', { ns: 'forms' })}
                    rules={[
                      {
                        required: true,
                        message: t('formValidation.selectRequired', {
                          ns: 'forms',
                        }),
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      mode="single"
                      placeholder={t('workFromHomeMethod', { ns: 'forms' })}
                      className="w-full"
                      loading={false}
                      options={getTranslatedCalculationOptionsForType(
                        'workFromHome',
                        t
                      )}
                      disabled={workFromHomeAllowanceState?.disabled}
                    />
                  </Form.Item>
                )}

                {workFromHomeAllowanceCurrencyState?.visible && (
                  <Form.Item
                    name={workFromHomeAllowanceCurrencyKeys}
                    label={t('workFromHomeCurrency', { ns: 'forms' })}
                    dependencies={[workFromHomeAllowanceKeys]}
                    rules={[
                      currencyRequiredWhenUsed(workFromHomeAllowanceKeys),
                    ]}
                  >
                    <Select
                      mode="single"
                      placeholder={t('workFromHomeCurrency', { ns: 'forms' })}
                      className="w-full"
                      loading={false}
                      options={currencyOptions}
                      disabled={workFromHomeAllowanceCurrencyState?.disabled}
                    />
                  </Form.Item>
                )}
              </div>

              {workFromHomeAllowanceState?.visible ||
              workFromHomeAllowanceCurrencyState?.visible ? (
                <hr className="mb-5" />
              ) : null}
            </>
          )}
        </div>
      )}
    </>
  );
}
